import { css } from "@emotion/css";
import { Button, Callout, CircularProgress, Collapse, Badge } from "@octopusdeploy/design-system-components";
import { ArrowRotateIcon, ChevronDownIcon, ChevronUpIcon, ExperimentIcon, SparklesIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, fontSize, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useStreamingQuery } from "@octopusdeploy/octopus-react-client";
import type { ErrorEvent, RecoveryAgentStreamEvent, ToolResponseEvent } from "@octopusdeploy/octopus-server-client";
import { isErrorEvent, isTextChunkEvent, isToolCallEvent, isToolResponseEvent, isMetadataEvent, isCompleteMessageEvent } from "@octopusdeploy/octopus-server-client";
import React, { useState, useEffect, useRef } from "react";
import Markdown from "~/components/Markdown/index";
import { RecoveryAgentFeedback } from "./RecoveryAgentFeedback";
import { ToolCallDisplay } from "./ToolCallDisplay";
const AnimatedDots = () => {
    const [dots, setDots] = useState(".");
    useEffect(() => {
        const interval = setInterval(() => {
            setDots((prev) => {
                if (prev === ".")
                    return "..";
                if (prev === "..")
                    return "...";
                return ".";
            });
        }, 500);
        return () => clearInterval(interval);
    }, []);
    return <span>{dots}</span>;
};
const BlinkingCursor = () => {
    return <span className={styles.blinkingCursor}>|</span>;
};
type CollapsiblePanelProps = {
    header: React.ReactNode;
    children: React.ReactNode;
    defaultCollapsed?: boolean;
};
const CollapsiblePanel = ({ header, children, defaultCollapsed = false }: CollapsiblePanelProps) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    const toggleCollapse = () => setIsCollapsed((prev) => !prev);
    return (<div className={styles.sidebarContainer}>
            <div className={styles.sidebarHeader} onClick={toggleCollapse} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && toggleCollapse()}>
                <div className={styles.sidebarTitleContainer}>{header}</div>
                <Button icon={isCollapsed ? <ChevronDownIcon size={16}/> : <ChevronUpIcon size={16}/>} onClick={(e) => {
            e.stopPropagation();
            toggleCollapse();
        }} label="" importance="ghost" size="small" aria-label={isCollapsed ? "Expand" : "Collapse"}/>
            </div>

            <Collapse in={!isCollapsed}>
                <div>{children}</div>
            </Collapse>
        </div>);
};
type LoadingStateProps = {
    text?: string;
};
const LoadingState = ({ text = "Loading" }: LoadingStateProps) => {
    return (<div className={styles.loadingContainer}>
            <CircularProgress size="small" aria-label={text}/>
            <div className={styles.loadingText}>
                {text}
                <AnimatedDots />
                <BlinkingCursor />
            </div>
        </div>);
};
type EmptyStateProps = {
    text?: string;
    icon?: React.ReactNode;
    action?: React.ReactNode;
};
const EmptyState = ({ text, icon, action }: EmptyStateProps) => {
    return (<div className={styles.emptyStateContainer}>
            {icon}
            {text && <p className={styles.emptyStateText}>{text}</p>}
            {action}
        </div>);
};
type ErrorStateProps = {
    error: string;
    onRetry?: () => void;
    isLoading?: boolean;
};
const ErrorState = ({ error, onRetry, isLoading }: ErrorStateProps) => {
    return (<div className={styles.emptyStateContainer}>
            <div className={styles.errorText}>{error}</div>
            {onRetry && <Button importance="secondary" label="Try again" onClick={onRetry} disabled={isLoading} size="medium"/>}
        </div>);
};
type RecoveryAgentHeaderProps = {
    showRetryButton?: boolean;
    onRetry?: () => void;
};
export const RecoveryAgentHeader = ({ showRetryButton, onRetry }: RecoveryAgentHeaderProps) => {
    return (<>
            <SparklesIcon size={20} color={themeTokens.color.icon.primary}/>
            <h3 className={styles.sidebarTitle}>Recovery Agent</h3>
            <Badge variant="feature" icon={<ExperimentIcon size={16}/>} size="small" label="Alpha"/>
            {showRetryButton && onRetry && (<div className={styles.retryButtonContainer}>
                    <Button size="small" importance="quiet" icon={<ArrowRotateIcon size={16}/>} onClick={(e) => {
                e.stopPropagation();
                onRetry();
            }} label="Another suggestion" aria-label="Get another suggestion"/>
                </div>)}
        </>);
};
type PanelContentProps = {
    children: React.ReactNode;
};
export const PanelContent = ({ children }: PanelContentProps) => {
    return <div className={styles.contentContainer}>{children}</div>;
};
const getUserFriendlyErrorMessage = (traceId?: string): string => {
    const baseMessage = "An error occurred while analyzing the task log. Please wait a moment and try again.";
    if (traceId) {
        return `${baseMessage}\n\nIf this issue persists, please quote this trace ID when contacting support: ${traceId}`;
    }
    return baseMessage;
};
type DeploymentFailureSummaryProps = {
    spaceId: string | null | undefined;
    serverTaskId: string;
};
export const DeploymentFailureSummary = (props: DeploymentFailureSummaryProps) => {
    const [events, setEvents] = useState<RecoveryAgentStreamEvent[]>([]);
    const [metadata, setMetadata] = useState<{
        traceId: string;
        spanId: string;
    } | null>(null);
    const [streamError, setStreamError] = useState<ErrorEvent | null>(null);
    const [hasStarted, setHasStarted] = useState(false);
    const retryRef = React.useRef<boolean>(false);
    const [revealedLength, setRevealedLength] = useState(0);
    const previousRevealedLengthRef = useRef<number>(0);
    const { executeQuery, isLoading, error } = useStreamingQuery<[
    ], RecoveryAgentStreamEvent | null>({
        name: "Analyze deployment failure",
        executeQuery: (repository) => repository.Tasks.diagnoseFailure(props.serverTaskId, retryRef.current, props.spaceId),
        onEventReceived: (event) => {
            if (event === null) {
                return;
            }
            if (isMetadataEvent(event)) {
                setMetadata({ traceId: event.traceId, spanId: event.spanId });
            }
            if (isErrorEvent(event)) {
                setStreamError(event);
            }
            retryRef.current = false;
            setEvents((prevEvents) => [...prevEvents, event]);
        },
    });
    const handleStartAnalysis = () => {
        setHasStarted(true);
        retryRef.current = false; // First analysis should use retry=false
        setEvents([]);
        setMetadata(null);
        setStreamError(null);
        setRevealedLength(0);
        previousRevealedLengthRef.current = 0;
        void executeQuery();
    };
    const handleRetry = () => {
        retryRef.current = true;
        setEvents([]);
        setMetadata(null);
        setStreamError(null);
        setRevealedLength(0);
        previousRevealedLengthRef.current = 0;
        void executeQuery();
    };
    const textContent = events
        .filter((e) => isTextChunkEvent(e) || isCompleteMessageEvent(e))
        .map((e) => e.content)
        .join("");
    // Typewriter effect: gradually reveal characters as they stream in
    useEffect(() => {
        if (textContent.length > revealedLength) {
            const interval = setInterval(() => {
                setRevealedLength((prev) => {
                    const gap = textContent.length - prev;
                    // Adaptive speed: reveal faster if we're falling behind
                    let charsToReveal = 20;
                    if (gap > 200) {
                        charsToReveal = 30; // Very fast catch-up
                    }
                    else if (gap > 100) {
                        charsToReveal = 20; // Fast
                    }
                    else if (gap > 50) {
                        charsToReveal = 15; // Medium-fast
                    }
                    else {
                        charsToReveal = 10; // Normal speed for smooth effect
                    }
                    const next = prev + charsToReveal;
                    if (next >= textContent.length) {
                        clearInterval(interval);
                        return textContent.length;
                    }
                    return next;
                });
            }, 10); // Every 10ms for smoother updates
            return () => clearInterval(interval);
        }
    }, [textContent.length, revealedLength]);
    // Update the "old text" boundary at line breaks to avoid splitting markdown syntax
    useEffect(() => {
        if (revealedLength > previousRevealedLengthRef.current) {
            const timer = setTimeout(() => {
                // Find the last newline before revealedLength to split at a safe boundary
                const textUpToRevealed = textContent.substring(0, revealedLength);
                const lastNewlineIndex = textUpToRevealed.lastIndexOf("\n");
                // Only update if we found a newline and it's ahead of current position
                // Otherwise keep the current boundary to avoid breaking syntax mid-line
                if (lastNewlineIndex > previousRevealedLengthRef.current) {
                    previousRevealedLengthRef.current = lastNewlineIndex + 1; // +1 to include the newline
                }
            }, 200); // Small delay to let fade-in start
            return () => clearTimeout(timer);
        }
    }, [revealedLength, textContent]);
    const toolCalls = events.filter(isToolCallEvent);
    const toolResponses = events.filter(isToolResponseEvent);
    // Create a map of tool responses by tool call ID
    const toolResponseMap = new Map<string, ToolResponseEvent>();
    for (const response of toolResponses) {
        toolResponseMap.set(response.toolCallId, response);
    }
    const hasContent = textContent.length > 0 || toolCalls.length > 0;
    const hasHookError = error !== null;
    const hasStreamError = streamError !== null;
    const hasAnyError = hasHookError || hasStreamError;
    const shouldShowFeedbackForm = !isLoading && !hasAnyError && textContent.length > 0 && metadata !== null;
    const shouldShowRetryButton = !isLoading && !hasAnyError && textContent.length > 0;
    // Render the appropriate state
    const renderContent = () => {
        // Loading state with no content yet
        if (isLoading && !hasContent) {
            return <LoadingState text="Analyzing deployment logs"/>;
        }
        // Empty state - not started yet
        if (!hasStarted && !hasContent) {
            return <EmptyState text="Try our new recovery agent to assist in recovering from a failed deployment." action={<Button importance="secondary" label="Suggest a fix" onClick={handleStartAnalysis} size="small"/>}/>;
        }
        // Error state with no content
        if (hasStarted && !hasContent && hasAnyError) {
            return <ErrorState error={getUserFriendlyErrorMessage(metadata?.traceId)} onRetry={handleRetry} isLoading={isLoading}/>;
        }
        // Has content - show it
        if (hasContent) {
            return (<PanelContent>
                    {toolCalls.length > 0 && (<div className={styles.toolCallsSection}>
                            {toolCalls.map((toolCall) => (<ToolCallDisplay key={toolCall.id} toolCall={toolCall} toolResponse={toolResponseMap.get(toolCall.id)}/>))}
                        </div>)}

                    {textContent && (<div className={styles.textStreamContainer}>
                            {(() => {
                        // When loading is complete, render all text as one (no split)
                        if (!isLoading) {
                            return <Markdown markup={textContent}/>;
                        }
                        // While loading, split old/new for fade-in effect
                        const oldText = textContent.substring(0, previousRevealedLengthRef.current);
                        const newText = textContent.substring(previousRevealedLengthRef.current, revealedLength);
                        return (<>
                                        {oldText && <Markdown markup={oldText}/>}
                                        {newText && newText.trim() && (<span className={styles.typewriterFadeIn}>
                                                <Markdown markup={newText}/>
                                            </span>)}
                                    </>);
                    })()}
                            {isLoading && (<span className={styles.streamingIndicator}>
                                    <AnimatedDots />
                                    <BlinkingCursor />
                                </span>)}
                        </div>)}

                    {hasAnyError && (<div className={styles.errorCallout}>
                            <Callout type="danger" title="Analysis incomplete" actions={{
                        primary: {
                            label: "Try again",
                            onClick: handleRetry,
                            disabled: isLoading,
                        },
                    }}>
                                {getUserFriendlyErrorMessage(metadata?.traceId)}
                            </Callout>
                        </div>)}

                    {shouldShowFeedbackForm && <RecoveryAgentFeedback traceId={metadata.traceId} spanId={metadata.spanId}/>}
                </PanelContent>);
        }
        // Fallback - empty
        return null;
    };
    return <CollapsiblePanel header={<RecoveryAgentHeader showRetryButton={shouldShowRetryButton} onRetry={handleRetry}/>}>{renderContent()}</CollapsiblePanel>;
};
const styles = {
    sidebarContainer: css({
        display: "flex",
        flexDirection: "column",
        height: "100%",
    }),
    sidebarHeader: css({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: space[8],
        background: themeTokens.color.background.secondary.default,
        paddingRight: space[16],
        paddingLeft: space[16],
        paddingTop: space[8],
        paddingBottom: space[8],
        cursor: "pointer",
        userSelect: "none",
    }),
    sidebarTitleContainer: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
        minWidth: 0, // Allow text truncation
        "@media (max-width: 1024px)": {
            gap: space[6],
        },
    }),
    sidebarTitle: css({
        margin: 0,
        font: text.heading.small,
        color: themeTokens.color.text.primary,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        "@media (max-width: 1024px)": {
            fontSize: fontSize.medium,
        },
    }),
    headerActions: css({
        display: "flex",
        alignItems: "center",
        gap: space[4],
        flexShrink: 0,
    }),
    loadingContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
        paddingTop: space[12],
        paddingRight: space[16],
        paddingBottom: space[16],
        paddingLeft: space[16],
        minHeight: "130px",
        "@media (max-width: 1024px)": {
            gap: space[6],
            padding: space[16],
        },
    }),
    loadingText: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
        },
    }),
    contentContainer: css({
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        wordBreak: "break-word",
        paddingTop: space[12],
        paddingRight: space[16],
        paddingBottom: space[16],
        paddingLeft: space[16],
        width: "640px",
        minHeight: "130px",
        // Style markdown content
        "& p": {
            marginTop: 0,
            marginBottom: space[12],
        },
        "& > p:last-of-type, & > div:last-of-type p:last-child": {
            display: "inline",
            marginBottom: 0,
        },
        "& ul, & ol": {
            marginTop: space[8],
            marginBottom: space[12],
            paddingLeft: space[16],
            "@media (max-width: 1024px)": {
                paddingLeft: space[12],
            },
        },
        "& li": {
            marginBottom: space[4],
        },
        "& code": {
            backgroundColor: themeTokens.color.background.secondary.default,
            padding: `${space[2]} ${space[4]}`,
            borderRadius: borderRadius.small,
            fontSize: fontSize["small"],
            fontFamily: "monospace",
            whiteSpace: "nowrap",
        },
        "& pre": {
            backgroundColor: themeTokens.color.background.secondary.default,
            padding: space[12],
            borderRadius: borderRadius.medium,
            overflow: "auto",
            marginTop: space[8],
            marginBottom: space[12],
            "@media (max-width: 1024px)": {
                padding: space[8],
            },
        },
        "& pre code": {
            backgroundColor: "transparent",
            padding: 0,
            whiteSpace: "pre",
        },
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
            width: "100%",
            padding: space[16],
        },
    }),
    toolCallsSection: css({
        marginBottom: space[16],
        "@media (max-width: 1024px)": {
            marginBottom: space[12],
        },
    }),
    errorCallout: css({
        marginTop: space[16],
        "@media (max-width: 1024px)": {
            marginTop: space[12],
        },
    }),
    emptyStateContainer: css({
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "center",
        flex: 1,
        gap: space[16],
        paddingTop: space[12],
        paddingRight: space[16],
        paddingBottom: space[16],
        paddingLeft: space[16],
        textAlign: "left",
        minHeight: "130px",
        "@media (max-width: 1024px)": {
            gap: space[12],
            padding: space[16],
        },
    }),
    emptyStateText: css({
        margin: 0,
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
        },
    }),
    errorText: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.danger,
        marginTop: space[8],
        whiteSpace: "pre-line",
        "@media (max-width: 1024px)": {
            fontSize: fontSize.small,
        },
    }),
    retryButtonContainer: css({
        marginLeft: space[16],
    }),
    blinkingCursor: css({
        display: "inline-block",
        marginLeft: "2px",
        animation: "blink 0.8s ease-in-out infinite",
        "@keyframes blink": {
            "0%, 49%": {
                opacity: 1,
            },
            "50%, 100%": {
                opacity: 0,
            },
        },
    }),
    textStreamContainer: css({
        "& > div": {
            display: "inline",
        },
        "& p:last-child": {
            display: "inline",
            marginBottom: 0,
        },
        "& ul:last-child, & ol:last-child, & pre:last-child": {
            marginBottom: 0,
        },
    }),
    typewriterFadeIn: css({
        display: "inline",
        animation: "typewriterFade 0.75s ease-out",
        "@keyframes typewriterFade": {
            "0%": {
                opacity: 0,
            },
            "100%": {
                opacity: 1,
            },
        },
    }),
    streamingIndicator: css({
        display: "inline",
        whiteSpace: "nowrap",
        marginLeft: space[4],
    }),
};
